import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { zodResolver } from "@hookform/resolvers/zod";

import { WFPButton } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";

import { rejectDelivery } from "../../deliveryConfirmationApi";
import { responseError } from "../DeliveryCostNegotiationModal";
import { Offer } from "../OfferCard/interfaces";
import { OfferSummary } from "../OfferSummary";
import {
  createRejectDeliverySchema,
  MAX_REJECTION_COMMENT_LENGTH,
  type RejectDeliveryValues,
} from "./schema";

export interface ExchangeRejectionReason {
  id: number;
  text: string;
  translations?: Record<string, string>;
  active: boolean;
}

interface RejectDeliveryModalProps {
  offer: Offer;
  reasons: ExchangeRejectionReason[];
  onCancel: () => void;
  onRejected: () => void;
}

const DEFAULT_VALUES: RejectDeliveryValues = { reason: "", comment: "" };

export const RejectDeliveryModal = ({
  offer,
  reasons,
  onCancel,
  onRejected,
}: RejectDeliveryModalProps) => {
  const { t, i18n } = useTranslation();
  const [actionError, setActionError] = useState("");

  const resolver = useMemo(
    () =>
      zodResolver(
        createRejectDeliverySchema(
          t("traderDetails.form.mandatoryFieldBlankError"),
          t("rejectDeliveryModal.commentTooLong")
        )
      ),
    [t]
  );
  const {
    handleSubmit,
    register,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<RejectDeliveryValues>({ resolver, defaultValues: DEFAULT_VALUES });

  const selectedReason = watch("reason");

  const onSubmit = handleSubmit(async values => {
    setActionError("");
    try {
      await rejectDelivery(offer.id, Number(values.reason), values.comment.trim());
      onRejected();
    } catch (error) {
      setActionError(responseError(error, t("rejectDeliveryModal.error")));
    }
  });

  return (
    <Dialog open onOpenChange={open => !open && onCancel()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("rejectDeliveryModal.title")}</DialogTitle>
          <DialogDescription>
            {t("rejectDeliveryModal.description", { aggregator: offer.tagged_aggregator?.name })}
          </DialogDescription>
        </DialogHeader>

        <OfferSummary offer={offer} />

        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="delivery_rejection_reason" className="font-semibold text-foreground">
              {`${t("rejectDeliveryModal.reason")} *`}
            </Label>
            <Select
              value={selectedReason}
              onValueChange={value =>
                setValue("reason", value, { shouldValidate: true, shouldDirty: true })
              }
            >
              <SelectTrigger
                id="delivery_rejection_reason"
                className="w-full"
                aria-invalid={!!errors.reason}
              >
                <SelectValue placeholder={t("rejectDeliveryModal.reasonPlaceholder")} />
              </SelectTrigger>
              <SelectContent position="popper" className="max-h-72">
                {reasons
                  .filter(reason => reason.active)
                  .map(reason => (
                    <SelectItem key={reason.id} value={String(reason.id)}>
                      {reason.translations?.[i18n.language] ?? reason.text}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {errors.reason && (
              <p role="alert" className="text-destructive text-sm">
                {errors.reason.message}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="delivery_rejection_comment" className="font-semibold text-foreground">
              {t("rejectDeliveryModal.comment")}
            </Label>
            <Textarea
              id="delivery_rejection_comment"
              maxLength={MAX_REJECTION_COMMENT_LENGTH}
              placeholder={t("rejectDeliveryModal.commentPlaceholder")}
              aria-invalid={!!errors.comment}
              {...register("comment")}
            />
            {errors.comment && (
              <p role="alert" className="text-destructive text-sm">
                {errors.comment.message}
              </p>
            )}
          </div>

          {actionError && (
            <p role="alert" className="text-destructive text-sm">
              {actionError}
            </p>
          )}

          <DialogFooter>
            <WFPButton type="button" variant="secondary" onClick={onCancel}>
              {t("common.cancel")}
            </WFPButton>
            <WFPButton type="submit" variant="destructive" disabled={isSubmitting}>
              {isSubmitting && <Spinner className="size-4" />}
              {t("rejectDeliveryModal.submit")}
            </WFPButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

interface ExchangeRejectionReasonsState {
  listExchangeRejectionReasons?: { data?: { results?: ExchangeRejectionReason[] } };
}

export const ConnectedRejectDeliveryModal = connect((state: ExchangeRejectionReasonsState) => ({
  reasons: state.listExchangeRejectionReasons?.data?.results ?? [],
}))(RejectDeliveryModal);
